import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import useSeo from "@/hooks/useSeo";
import { SITE_NAME, SITE_URL, allTopics, formatHeat } from "@/lib/site";
import { siteStats } from "@/mocks/siteStats";
import {
  aboutContact,
  aboutIntro,
  aboutMilestones,
  aboutPositioning,
  aboutPrinciples,
  aboutTestimonials,
} from "@/mocks/about";

export default function About() {
  const topHeat = formatHeat(Math.max(...allTopics.map((topic) => topic.heat)));

  useSeo({
    title: "关于我们_事件追踪定位与内容原则｜瓜田雷达",
    description:
      "瓜田雷达是一个专注于网络热点事件进展追踪的内容站。了解我们的网站定位、内容原则、发展历程、用户口碑与联系方式，以及我们如何整理网曝吃瓜的进展与后续信息。",
    keywords: "关于我们,内容原则,事件追踪定位,信息来源,瓜田雷达,吃瓜追踪",
    path: "/about",
    breadcrumbs: [
      { name: "首页", path: "/" },
      { name: "关于我们", path: "/about" },
    ],
    jsonLd: [
      {
        "@type": "AboutPage",
        url: `${SITE_URL}/about`,
        inLanguage: "zh-CN",
        name: `关于${SITE_NAME}`,
        description: `${SITE_NAME}的网站定位、内容原则与发展历程。`,
      },
      {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
        email: aboutContact.email,
        description:
          "以时间线方式整理网络热点事件的进展与后续，只呈现可追溯的公开信息，不做评价与定性。",
      },
    ],
  });

  return (
    <>
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="px-4 py-8 md:px-8 md:py-14">
            <Breadcrumbs
              items={[
                { name: "首页", path: "/" },
                { name: "关于我们", path: "/about" },
              ]}
            />
            <div className="mt-6 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                  <i className="ri-radar-line" aria-hidden="true" />
                  自 2019 年开始整理事件时间线
                </span>
                <h1 className="mt-5 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-[36px]">
                  关于瓜田雷达
                </h1>
                <div className="mt-5 space-y-4">
                  {aboutIntro.map((paragraph) => (
                    <p
                      key={paragraph.slice(0, 20)}
                      className="text-sm leading-[1.9] text-foreground-700 md:text-[15px]"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/topics"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-archive-2-line" aria-hidden="true" />
                    浏览话题档案
                  </Link>
                  <a
                    href={`mailto:${aboutContact.email}`}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                  >
                    <i className="ri-mail-send-line" aria-hidden="true" />
                    与我们联系
                  </a>
                </div>
              </div>

              <figure className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="h-56 w-full md:h-72">
                  <img
                    src="https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20of%20stacked%20archival%20folders%20and%20timeline%20documents%20on%20warm%20cream%20paper%2C%20coral%20and%20amber%20accents%2C%20minimal%20flat%20design%2C%20clean%20balanced%20composition&width=1200&height=800&seq=gt-about-01&orientation=landscape"
                    alt="瓜田雷达事件档案与时间线整理示意图"
                    title="瓜田雷达 - 事件档案与时间线整理"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <figcaption className="border-t border-background-200 px-4 py-3 text-xs text-foreground-500">
                  目前站内收录事件 {allTopics.length} 份，单份档案最高热度 {topHeat}
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="px-4 py-12 md:px-8 md:py-16">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {siteStats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <p className="font-heading text-3xl font-black text-foreground-950">
                    {stat.value}
                  </p>
                  <p className="mt-2 font-heading text-sm font-semibold text-foreground-800">
                    {stat.label}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="px-4 py-12 md:px-8 md:py-16">
            <p className="flex items-center gap-2 text-xs font-label uppercase tracking-[0.2em] text-primary-600">
              <span className="inline-block h-[2px] w-6 bg-primary-500" aria-hidden="true" />
              Positioning
            </p>
            <h2 className="mt-3 max-w-2xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
              我们的网站定位
            </h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {aboutPositioning.map((item) => (
                <article
                  key={item.title}
                  className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md border border-accent-200 bg-accent-100 text-accent-800">
                    <i className={`${item.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="px-4 py-12 md:px-8 md:py-16">
            <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr]">
              <div className="lg:sticky lg:top-28 lg:self-start">
                <p className="flex items-center gap-2 text-xs font-label uppercase tracking-[0.2em] text-primary-600">
                  <span className="inline-block h-[2px] w-6 bg-primary-500" aria-hidden="true" />
                  Principles
                </p>
                <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
                  内容原则
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  这六条是所有档案共同的写作规范。它们决定了什么内容会被写进时间线，什么内容会被留在外面。
                </p>
              </div>
              <ol className="grid gap-4 sm:grid-cols-2">
                {aboutPrinciples.map((principle, index) => (
                  <li
                    key={principle}
                    className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {index + 1}
                    </span>
                    <span className="text-sm leading-relaxed text-foreground-700">{principle}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="px-4 py-12 md:px-8 md:py-16">
            <p className="flex items-center gap-2 text-xs font-label uppercase tracking-[0.2em] text-primary-600">
              <span className="inline-block h-[2px] w-6 bg-primary-500" aria-hidden="true" />
              Milestones
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
              发展历程
            </h2>
            <ol className="mt-8 grid gap-5 md:grid-cols-3 lg:grid-cols-5">
              {aboutMilestones.map((item) => (
                <li
                  key={item.year}
                  className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="font-heading text-xl font-black text-primary-600">
                    {item.year}
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-bold leading-snug text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="px-4 py-12 md:px-8 md:py-16">
            <p className="flex items-center gap-2 text-xs font-label uppercase tracking-[0.2em] text-primary-600">
              <span className="inline-block h-[2px] w-6 bg-primary-500" aria-hidden="true" />
              Readers
            </p>
            <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
              读者怎么说
            </h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              {aboutTestimonials.map((item) => (
                <blockquote
                  key={item.name}
                  className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6"
                >
                  <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true" />
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                    {item.quote}
                  </p>
                  <footer className="mt-5 border-t border-background-200 pt-4 text-xs text-foreground-500">
                    <span className="font-medium text-foreground-800">{item.name}</span>
                    <span className="mx-2 text-foreground-300">|</span>
                    {item.role}
                  </footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="scroll-mt-28 bg-foreground-950">
          <div className="px-4 py-12 md:px-8 md:py-16">
            <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
              <div>
                <p className="flex items-center gap-2 text-xs font-label uppercase tracking-[0.2em] text-primary-300">
                  <span className="inline-block h-[2px] w-6 bg-primary-400" aria-hidden="true" />
                  Contact
                </p>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-[30px]">
                  联系我们
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-100/80">
                  无论是内容纠错、话题建议还是合作咨询，都可以通过下面的邮箱联系。我们来信较多，通常会在两个工作日内回复。
                </p>
                <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-background-50/20 px-3 py-1 text-xs text-background-100/80">
                  <i className="ri-time-line" aria-hidden="true" />
                  {aboutContact.hours}
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-1">
                <a
                  href={`mailto:${aboutContact.email}`}
                  className="flex items-center gap-4 rounded-lg border border-background-50/15 bg-background-50/5 p-5 transition-colors hover:border-primary-400"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className="ri-mail-line text-lg" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs text-background-100/70">一般咨询与建议</span>
                    <span className="mt-1 block text-sm font-medium text-background-50">
                      {aboutContact.email}
                    </span>
                  </span>
                </a>
                <a
                  href={`mailto:${aboutContact.correctionEmail}`}
                  className="flex items-center gap-4 rounded-lg border border-background-50/15 bg-background-50/5 p-5 transition-colors hover:border-primary-400"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                    <i className="ri-error-warning-line text-lg" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs text-background-100/70">内容纠错</span>
                    <span className="mt-1 block text-sm font-medium text-background-50">
                      {aboutContact.correctionEmail}
                    </span>
                  </span>
                </a>
                <a
                  href={`mailto:${aboutContact.partnershipEmail}`}
                  className="flex items-center gap-4 rounded-lg border border-background-50/15 bg-background-50/5 p-5 transition-colors hover:border-primary-400"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                    <i className="ri-briefcase-4-line text-lg" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs text-background-100/70">内容合作</span>
                    <span className="mt-1 block text-sm font-medium text-background-50">
                      {aboutContact.partnershipEmail}
                    </span>
                  </span>
                </a>
              </div>
            </div>
            <div className="mt-10 border-t border-background-50/10 pt-6">
              <Link
                to="/topics"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-archive-2-line" aria-hidden="true" />
                去看正在追踪的事件
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}